import { getBlockchainRpc } from "@talentir/shared/helpers";
import { ChainNotConfiguredError, createConnector } from "@wagmi/core";
import { type JsonRpcError, Magic, RPCErrorCode } from "magic-sdk";
import { type Chain, SwitchChainError, createWalletClient, custom, getAddress, toHex } from "viem";
import { normalizeChainId } from "wagmi";

type ExcludeUndefined<T> = T extends undefined ? never : T;

export type MagicConnectorAdditions = {
    setEmail: (email: string) => void;
    setOnOtpEnterReady: (onOtpEnterReadyFn: (submitToken: (token: string) => void, isRetry: boolean) => void) => void;
    cancel: () => void;
};

export function hasFunctionProperty<T>(obj: unknown, propName: string): obj is T {
    try {
        return obj != null && typeof obj === "object" && typeof (obj as never)[propName] === "function";
    } catch {
        return false;
    }
}

export function magicConnector() {
    let magic: { sdk: Magic; chainId: number } | undefined;

    type Provider = ExcludeUndefined<typeof magic>["sdk"]["rpcProvider"];

    return createConnector<Provider, MagicConnectorAdditions>((config) => {
        const getMagicSDK = (chaindId?: number) => {
            if (magic == null || (chaindId != null && magic.chainId !== chaindId)) {
                const chain = config.chains.find((x) => x.id === chaindId) ?? config.chains[0];
                if (chain == null) throw new Error("Chain not supported");

                magic = createMagicSdk(chain);
            }

            return magic;
        };

        let onOtpEnterReady: ((submitToken: (token: string) => void, isRetry: boolean) => void) | undefined;
        let email = "";
        let currentHandle: ReturnType<Magic["auth"]["loginWithEmailOTP"]> | undefined;

        return {
            id: "magic",
            name: "Magic Wallet",
            type: "magicWallet" as const,
            async connect({ chainId } = {}) {
                try {
                    const provider = getMagicSDK(chainId).sdk.rpcProvider;

                    provider.on("disconnect", () => {
                        this.onDisconnect();
                    });

                    // Check if there is a user logged in
                    const isAuthenticated = await this.isAuthorized();

                    // if there is a user logged in, return the user
                    if (isAuthenticated) {
                        let currentChainId = magic?.chainId ?? 0;
                        if (chainId != null && currentChainId !== chainId) {
                            const chain = await this.switchChain?.({ chainId });
                            currentChainId = chain?.id ?? currentChainId;
                        }

                        return {
                            accounts: await this.getAccounts(),
                            chainId: currentChainId,
                        };
                    }

                    // open the modal and process the magic login steps
                    const localMagic = getMagicSDK(chainId);

                    const handle = localMagic.sdk.auth.loginWithEmailOTP({
                        email,
                        showUI: false,
                    });
                    currentHandle = handle;

                    await new Promise((resolve) => {
                        void handle.on("email-otp-sent", () => {
                            if (onOtpEnterReady == null) {
                                throw new Error("onOtpEnterReady function is not set");
                            }

                            onOtpEnterReady((token) => {
                                handle.emit("verify-email-otp", token);
                                resolve(undefined);
                            }, false);
                        });
                    });

                    await new Promise((resolve, reject) => {
                        try {
                            void handle
                                .on("done", () => {
                                    resolve(undefined);
                                })
                                .on("invalid-email-otp", () => {
                                    // When user enters invalid OTP, we need to ask them to enter it again
                                    if (onOtpEnterReady == null) {
                                        throw new Error("onOtpEnterReady function is not set");
                                    }

                                    onOtpEnterReady((token) => {
                                        handle.emit("verify-email-otp", token);
                                    }, true);
                                })
                                .on("error", (reason) => {
                                    reject(new Error(reason as string));
                                });
                        } catch (e) {
                            reject(e);
                        }
                    });

                    return {
                        accounts: await this.getAccounts(),
                        chainId: chainId ?? 0,
                    };
                } catch (e) {
                    const magicError = e as JsonRpcError;
                    if (magicError.code === RPCErrorCode.InvalidParams) {
                        throw new Error("Email is not valid");
                    }

                    throw new Error(magicError.message);
                } finally {
                    onOtpEnterReady = undefined;
                    currentHandle = undefined;
                }
            },
            async disconnect() {
                if (magic == null) return;

                await magic.sdk.user.logout();
                magic = undefined;
            },
            async getAccounts() {
                const provider = await this.getProvider();
                return (
                    await provider.request<string[]>({
                        method: "eth_accounts",
                    })
                ).map((x) => getAddress(x));
            },
            async getChainId() {
                const provider = await this.getProvider();
                const chainId = await provider.request<number>({ method: "eth_chainId" });
                return normalizeChainId(chainId);
            },
            async getProvider() {
                const magic = getMagicSDK();
                return magic.sdk.rpcProvider;
            },
            async isAuthorized() {
                try {
                    if (!(await getMagicSDK().sdk.user.isLoggedIn())) {
                        throw new Error();
                    }

                    const account = await this.getAccounts();
                    return account.length !== 0;
                } catch {
                    return false;
                }
            },
            async switchChain({ chainId }) {
                const chain = config.chains.find((x) => x.id === chainId);

                if (chain == null) throw new SwitchChainError(new ChainNotConfiguredError());

                getMagicSDK(chainId);
                this.onChainChanged(toHex(chainId));

                return chain;
            },
            onAccountsChanged(accounts) {
                if (accounts.length === 0) {
                    config.emitter.emit("disconnect");
                } else {
                    config.emitter.emit("change", {
                        accounts: accounts.map((x) => getAddress(x)),
                    });
                }
            },
            onChainChanged(chain) {
                const chainId = normalizeChainId(chain);
                config.emitter.emit("change", { chainId });
            },
            onDisconnect() {
                config.emitter.emit("disconnect");

                getMagicSDK().sdk.rpcProvider.removeListener("disconnect", this.onDisconnect.bind(this));
            },
            async getClient(data) {
                const provider = await this.getProvider();
                const accounts = await this.getAccounts();
                const account = accounts[0];
                const chain = config.chains.find((x) => x.id === data?.chainId) ?? config.chains[0];
                if (chain == null) {
                    throw new Error("Chain not supported");
                }

                return createWalletClient({
                    account,
                    chain,
                    transport: custom({
                        async request({ method, params }) {
                            return await provider.request({
                                method,
                                params,
                            });
                        },
                    }),
                });
            },
            setOnOtpEnterReady: (
                onOtpEnterReadyFn: (submitToken: (token: string) => void, isRetry: boolean) => void,
            ) => {
                onOtpEnterReady = onOtpEnterReadyFn;
            },
            setEmail: (newEmail: string) => {
                email = newEmail;
            },
            cancel: () => {
                if (currentHandle) {
                    currentHandle.emit("cancel");
                }
            },
        };
    });
}

function createMagicSdk(chain: Chain) {
    console.log("DEBUG: Creating Magic SDK for chain", getBlockchainRpc(chain));

    const blockchainRpc = getBlockchainRpc(chain);

    return {
        sdk: new Magic("pk_live_5AB0A9D428370044", {
            network: {
                rpcUrl: blockchainRpc,
                chainId: chain.id,
            },
        }),
        chainId: chain.id,
    };
}
